import { useCallback } from 'react';
import { cn } from '@/shared/lib/utils/classnames';
import { useYoutubePlayerStore } from '@/shared/store/youtube-player';
import type { CardBlock as CardBlockType } from '@/shared/types/blocks';
import { BlockRenderer } from '../BlockRenderer';

interface CardBlockProps extends Omit<CardBlockType, 'type' | 'id'> {
  onToolClick?: (toolId: string) => void;
  /** 연결된 YouTube 영상 ID (seconds와 함께 사용 시 카드 클릭으로 영상 점프) */
  linkedVideoId?: string;
}

/**
 * CardBlock - 카드 컨테이너
 *
 * Figma 디자인 기준:
 * - transparent: 배경/테두리 없음 (리포트 헤더용)
 * - highlight: 파란색 배경 (#0084ff) + 흰색 텍스트 + 가운데 정렬 (핵심 요약)
 * - muted: 회색 배경 (#f6f6f6) + 테두리 없음 (챕터/하이라이트 카드)
 * - default: 흰색 배경 + 테두리
 */
export function CardBlock({
  variant = 'default',
  items,
  background,
  padding,
  gap,
  seconds,
  onToolClick,
  linkedVideoId,
}: CardBlockProps) {
  const { requestSeek } = useYoutubePlayerStore();

  // seconds와 linkedVideoId가 모두 있으면 클릭 가능
  const isClickable = seconds !== undefined && linkedVideoId !== undefined;

  const handleClick = useCallback(() => {
    if (isClickable) {
      requestSeek(linkedVideoId, seconds);
    }
  }, [isClickable, linkedVideoId, seconds, requestSeek]);
  const variantConfig = {
    default: {
      className: 'bg-white border border-[#d9dde0]',
      defaultPadding: 16,
      gap: 'gap-2',
      textColor: '',
    },
    transparent: {
      className: '',
      defaultPadding: 0,
      gap: 'gap-3',
      textColor: '',
    },
    highlight: {
      className: 'bg-primary text-center',
      defaultPadding: 20,
      gap: 'gap-2',
      textColor:
        '[&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_p]:text-white [&_span]:text-white',
    },
    'primary-tint': {
      className: 'bg-[#e6f1ff]',
      defaultPadding: 16,
      gap: 'gap-2',
      textColor: '',
    },
    warning: {
      className: 'bg-amber-50 border border-amber-200',
      defaultPadding: 16,
      gap: 'gap-2',
      textColor: '',
    },
    success: {
      className: 'bg-green-50 border border-green-200',
      defaultPadding: 16,
      gap: 'gap-2',
      textColor: '',
    },
    muted: {
      className: 'bg-[#f6f6f6]',
      defaultPadding: 16,
      gap: 'gap-2',
      textColor: '',
    },
    compact: {
      className: 'bg-[#f6f6f6]',
      defaultPadding: 12,
      gap: 'gap-2',
      textColor: '',
    },
  }[variant];

  const effectivePadding = padding ?? variantConfig.defaultPadding;
  // 커스텀 gap이 제공되면 style로 적용, 아니면 variant 기본 클래스 사용
  const effectiveGapClass = gap !== undefined ? '' : variantConfig.gap;
  const gapStyle = gap !== undefined ? { gap: `${gap}px` } : undefined;

  // gap이 지정되면 flex 컨테이너 필요 (gap은 flex/grid에서만 동작)
  const needsFlex = gap !== undefined;

  const cardContent = (
    <BlockRenderer
      blocks={items}
      onToolClick={onToolClick}
      linkedVideoId={linkedVideoId}
      className={needsFlex ? `flex flex-col ${effectiveGapClass}` : effectiveGapClass}
      style={gapStyle}
      noFlex={!needsFlex}
    />
  );

  const cardClassName = cn(
    'rounded-[6px]',
    variantConfig.className,
    variantConfig.textColor,
    isClickable && 'cursor-pointer hover:opacity-90 transition-opacity',
  );

  const cardStyle = {
    padding: effectivePadding ? `${effectivePadding}px` : undefined,
    backgroundColor: background,
  };

  // 클릭 가능한 카드는 button으로 렌더링 (접근성)
  if (isClickable) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={cn(cardClassName, 'w-full text-left')}
        style={cardStyle}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div className={cardClassName} style={cardStyle}>
      {cardContent}
    </div>
  );
}
